import {
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useNavigationType, useLocation  } from 'react-router-dom';
import { font } from '../../Common/font';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import {
  deleteDieTool,
  fetchDieToolList,
  resetDieToolData,
  resetDieToolList,
} from '../../slices/die-tool';
import ImportDataModal from '../../Common/ImportDataModal';
import moment from 'moment';
import { debounce } from 'lodash';
import { createActivityLog } from '../../slices/activity-log';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import logo from '../../Images/alux-logo.png';
import formatFile from "../../Images/sample-files/die-tool-format.xlsx";

export default function DieToolList() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const ipAddress = useSelector(state => state.ip.ipAddress);
  const { data: settingData } = useSelector(state => state.setting);
  const { user: currentUser } = useSelector(state => state.auth);
  const { data = [], totalRecord, status } = useSelector(state => state.dieTool);
   const isMountedRef = useRef(true);
  const [search, setSearch] = useState(localStorage.getItem('die_text_search_tool') || '');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(100);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [checkedList, setCheckedList] = useState([
    'is_active',
    'run_under_deviation',
    'developer',
    'total_running_qty',
    'die_cavity',
    'die_size',
    'tool_number',
    'id',
    'action',
    'current_kg',
    'order_date',
    'received_date',
    'drawing_no',
    'developer_ref_no',
    'total_running_ton',
    'rac_no',
    'row_no',
    'column_no',
  ]);

  // Set mounted ref on component mount and cleanup on unmount
  useEffect(() => {
    console.log("First useEffect");
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    console.log("Second useEffect");
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [dispatch, ipAddress]);

  const debouncedSearch = useMemo(
    () => debounce((query) => {
      if (!isMountedRef.current) return;
      
      setIsSearching(true);
      
      if (query.length >= 3 || query.length === 0) {
        // Clear existing data before new search
        dispatch(resetDieToolData());
        
        dispatch(fetchDieToolList({ 
          page: 1, 
          pagesize,
          ...(query ? { search: query } : {}),
          ...(orderBy ? { ordering: orderBy } : {}),
        }))
        .finally(() => {
          if (isMountedRef.current) {
            setIsSearching(false);
          }
        });
        
        setPage(1); // Reset to first page on search
      } else {
        setIsSearching(false);
      }
    }, 300), // Reduced debounce time for better UX
    [dispatch, pagesize, orderBy]
  );

  // Initial data fetch and when dependencies change
  useEffect(() => {
    console.log("Third useEffect");
    if (!isMountedRef.current) return;

    // Clear data first for clean loading state
    dispatch(resetDieToolData());
    
    // Fetch new data
    dispatch(fetchDieToolList({
      page,
      pagesize,
      ...(search ? { search } : {}),
      ...(orderBy ? { ordering: orderBy } : {}),
    }));
  }, [dispatch, page, pagesize, orderBy]);

  

   // Cleanup debounced function
  useEffect(() => {
    console.log("Fourth useEffect");
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Optimized handlers with useCallback
  // Optimized handlers with useCallback
  const resetFilter = useCallback(() => {
    setSearch('');
    setOrderBy('');
    setPage(1);
    setIsSearching(false);
    localStorage.removeItem('die_text_search_tool');
    localStorage.removeItem('die_group_search_tool');
    
    // Clear data first then fetch
    dispatch(resetDieToolData());
    dispatch(fetchDieToolList({ page: 1, pagesize }));
  }, [dispatch, pagesize]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((current, size) => {
    setPage(1);
    setPageSize(size);
  }, []);

  // Optimized sorting function using lookup object
  const sortingMap = useMemo(() => ({
    'tool_number': { asc: 'tool_number', desc: '-tool_number' },
    'die_size': { asc: 'die_size', desc: '-die_size' },
    'order_date': { asc: 'order_date', desc: '-order_date' },
    'received_date': { asc: 'received_date', desc: '-received_date' },
    'current_kg': { asc: 'current_kg', desc: '-current_kg' },
    'die_cavity': { asc: 'die_cavity', desc: '-die_cavity' },
    'is_active': { asc: 'is_active', desc: '-is_active' },
    'die_sub_category': { asc: 'die_sub_category', desc: '-die_sub_category' },
    'total_running_qty': { asc: 'total_running_qty', desc: '-total_running_qty' },
    'rac_no': { asc: 'rac_no', desc: '-rac_no' },
    'row_no': { asc: 'row_no', desc: '-row_no' },
    'column_no': { asc: 'column_no', desc: '-column_no' },
    'developer': { asc: 'developer', desc: '-developer' },
    'run_under_deviation': { asc: 'run_under_deviation', desc: '-run_under_deviation' },
    'purchase_price': { asc: 'purchase_price', desc: '-purchase_price' },
    'remarks': { asc: 'remarks', desc: '-remarks' },
    'drawing_no': { asc: 'drawing_no', desc: '-drawing_no' },
    'total_running_ton': { asc: 'total_running_ton', desc: '-total_running_ton' },
    'ownership': { asc: 'ownership', desc: '-ownership' },
    'developer_ref_no': { asc: 'developer_ref_no', desc: '-developer_ref_no' },
  }), []);

 const GetSortingEnumValue = useCallback((sortField, sortOrder) => {
    const field = sortingMap[sortField];
    if (field) {
      return sortOrder === 'ascend' ? field.asc : field.desc;
    }
    return 'id';
  }, [sortingMap]);


 const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const newOrderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(newOrderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  }, [GetSortingEnumValue]);

 const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    
    // Clear data immediately for better UX
    if (value.length >= 3 || value.length === 0) {
      dispatch(resetDieToolData());
    }
    
    // Trigger debounced search
    debouncedSearch(value);
  }, [debouncedSearch, dispatch]);

  const refreshTable = () => {
    dispatch(resetDieToolData());
    dispatch(
      fetchDieToolList({
        page: 1,
        pagesize,
        ...(search && { search }),
        ...(orderBy && { ordering: orderBy }),
      })
    );
  };
  const handleEdit = useCallback((data) => {
    navigate(`/edit-die-tool/${data.id}`);
  }, [navigate]);

  const handleDelete = useCallback((id) => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deleteDieTool(id))
      .unwrap()
      .then(() => {
        message.success('Die Tool Archive Successfully!');
      })
      .catch(err => {
        console.error('Delete Error:', err);
        message.error(`Error: ${err || 'Failed to delete the profile.'}`);
      });
  }, [dispatch]);

  const debouncedAPICall = useMemo(() =>
    debounce((query) => {
      if (query.length >= 3) {
        dispatch(fetchDieToolList({ search: query }));
        localStorage.setItem('die_text_search_tool', query);
      }
    }, 1000), [dispatch]
  );

  useEffect(() => {
    console.log("Fifth useEffect");
    return () => {
      debouncedAPICall.cancel(); // Clean up on unmount
    };
  }, [debouncedAPICall]);

  const deviationStatusMap = {
    active: 'Active',
    inactive: 'Inactive',
    die_broken: 'Die Broken',
    die_absolute: 'Die Absolute',
    die_shift: 'Die Shift',
    other: 'Other',
  };

 const handleColumnVisibilityChange = useCallback((checkedValues) => {
    setCheckedList(checkedValues);
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Sr. No.',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      render: (text, record, index) => (page - 1) * pagesize + index + 1,
      sorter: false,
      align: 'center'
    },
    {
      title: 'Tool No.',
      dataIndex: 'tool_number',
      key: 'tool_number',
      sorter: true,
      width: '80px',
      render: (text, record) => (
        <span>
          {record?.die?.die_number
            ? `${record.die.die_number}-${record.tool_number}`
            : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Tool Size',
      dataIndex: 'die_size',
      key: 'die_size',
      width: '100px',
      render: record => (
        <span>
          {record?.die_width
            ? `${record.die_width} X ${record.die_height}`
            : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Current KG/MTR',
      dataIndex: 'current_kg',
      key: 'current_kg',
      width: '100px',
      render: (text, record) => (
        <span>
          {record?.current_kg || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      width: '100px',
      key: 'order_date',
      sorter: true,
      render: record => (
        <span>
          {record ? dayjs(record).format('DD-MM-YYYY') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Received Date',
      dataIndex: 'received_date',
      key: 'received_date',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record ? dayjs(record).format('DD-MM-YYYY') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Die Cavity',
      dataIndex: 'die_cavity',
      width: '100px',
      key: 'die_cavity',
      sorter: true,
      render: die_cavity => <span>{die_cavity ?? 'N/A'}</span>,
    },
    {
      title: 'Die Received Status',
      dataIndex: 'is_active',
      width: '100px',
      key: 'is_active',
      render: status => (status === true ? 'Received' : 'Not Received'),
      sorter: true,
    },
    {
      title: 'Total Running QTY (kg)',
      dataIndex: 'total_running_qty',
      key: 'total_running_qty',
      width: '100px',
      sorter: true,
    },
    {
      title: 'Rack No.',
      dataIndex: 'rac_no',
      key: 'rac_no',
      width: '100px',
      sorter: true,
      render: (text, record) => <span>{record.rac_no ?? 'N/A'}</span>,
    },
    {
      title: 'Row No.',
      dataIndex: 'row_no',
      key: 'row_no',
      width: '100px',
      sorter: true,
      render: (text, record) => <span>{record.row_no ?? 'N/A'}</span>,
    },
    {
      title: 'Column No.',
      dataIndex: 'column_no',
      key: 'column_no',
      width: '100px',
      sorter: true,
      render: (text, record) => <span>{record.column_no ?? 'N/A'}</span>,
    },
    {
      title: 'Vendor',
      dataIndex: 'developer',
      width: '100px',
      key: 'developer',
      render: (text, record) => (
        <span>
          {record.developer?.customer_name || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Ownership',
      width: '100px',
      dataIndex: 'ownership',
      key: 'ownership',
      sorter: true,
     render: (text, record) => (
        <span>
          {record?.ownership === "own"  ? settingData?.company_settings?.company_name : record.ownership|| 'N/A'}
        </span>
      ),
    },
    {
      title: 'Drawing No.',
      dataIndex: 'drawing_no',
      key: 'drawing_no',
      width: '100px',
      sorter: true,
      render: drawing_no => <span>{drawing_no || 'N/A'}</span>,
    },
    {
      title: 'Total Running Metric Ton',
      dataIndex: 'total_running_ton',
      key: 'total_running_ton',
      width: '100px',
      sorter: true,
      render: total_running_ton => <span>{total_running_ton || 'N/A'}</span>,
    },
    {
      title: 'Developer Ref. No',
      dataIndex: 'developer_ref_no',
      key: 'developer_ref_no',
      width: '100px',
      sorter: true,
      render: ownership => <span>{ownership || 'N/A'}</span>,
    },
    {
      title: 'Run Under Deviation',
      dataIndex: 'run_under_deviation',
      key: 'run_under_deviation',
      width: '100px',
      sorter: true,
      render: run_under_deviation => <span>{run_under_deviation || 'N/A'}</span>,
    },
    {
      title: 'Price',
      dataIndex: 'purchase_price',
      width: '100px',
      key: 'purchase_price',
      sorter: true,
      render: (text, record) => (
        <p style={{ textAlign: 'end' }}>
          {`₹ ${record.purchase_price?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || 0}`}
        </p>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      sorter: true,
      width: '100px',
      render: remarks => <span>{remarks || 'N/A'}</span>,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record ? `${record.first_name} ${record.last_name}` : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record?.trim() ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record?.first_name?.trim() && record?.last_name?.trim()
            ? `${record.first_name} ${record.last_name}`
            : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Die Life Card',
      dataIndex: 'id',
      key: 'id',
      width: '100px',
      render: id => (
        <Space>
          {currentUser.permissions.permissions.die.includes('view_dietool') && (
            <Tooltip placement="top" title={'View Die Life Card'}>
              <Link to={`/die-life-card/${id}`} target="_blank">
                <FileTextOutlined />
              </Link>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'action',
      width: '100px',
      fixed: 'right',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.die.includes('change_dietool') && (
            <Tooltip placement="top" title={'Edit'}>
              <span onClick={() => handleEdit(record)}>
                <EditIcon style={{ cursor: 'pointer' }} />
              </span>
            </Tooltip>
          )}
          {currentUser.permissions.permissions.die.includes('delete_dietool') && (
            <Tooltip placement="top" title={'Archive'}>
              <Popconfirm
                title="Are you sure you want to archive this record?"
                onConfirm={() => handleDelete(record.id)}
                onCancel={() => message.info('Action cancelled')}
                okText="Yes"
                cancelText="No"
              >
                <Archive style={{ cursor: 'pointer' }} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ], [page, pagesize, currentUser, handleEdit, handleDelete]);

  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Checkbox value="id" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="tool_number">Tool No</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="die_size">Tool Size</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="current_kg">Current KG/MTR</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="received_date">Recived Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="order_date">Order Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="die_cavity">Die Cavity</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="is_active">Die Received Status</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="total_running_qty">
                Total Running QTY (kg)
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="rac_no">Rack Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="row_no">Row Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="column_no">Column Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="developer">Vendor</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="ownership">Ownership</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="drawing_no">Drawing No.</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="total_running_ton">
                Total Running Metric Ton
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="run_under_deviation">
                Run Under Deviation
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="purchase_price">Purchase Price</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="remarks">Remarks</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );
  const exportDieToolToPDF = pdfdata => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    pdf.addFileToVFS('WorkSans-normal.ttf', font);
    pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    pdf.setFont('WorkSans');

    const allColumns = [
      { header: 'Tool No.', dataKey: 'toolNumber', field: 'tool_number' },
      { header: 'Tool Size', dataKey: 'toolSize', field: 'die_size' },
      { header: 'Current KG/MTR', dataKey: 'current_kg', field: 'current_kg' },
      { header: 'Recived Date', dataKey: 'received_date', field: 'received_date' },
      { header: 'Order Date', dataKey: 'order_date', field: 'order_date' },
      { header: 'Die Cavity', dataKey: 'dieCavity', field: 'die_cavity' },
      { header: 'Die Received Status', dataKey: 'status', field: 'is_active' },
      { header: 'Total Running QTY (kg)', dataKey: 'runningQty', field: 'total_running_qty' },
      { header: 'Rack No.', dataKey: 'rac_no', field: 'rac_no' },
      { header: 'Row No.', dataKey: 'row_no', field: 'row_no' },
      { header: 'Column No.', dataKey: 'column_no', field: 'column_no' },
      { header: 'Vendor', dataKey: 'developer', field: 'developer' },
      { header: 'Ownership', dataKey: 'ownership', field: 'ownership' },
      { header: 'Drawing Number', dataKey: 'drawing_no', field: 'drawing_no' },
      { header: 'Developer Refrence Number', dataKey: 'developer_ref_no', field: 'developer_ref_no' },
      { header: 'Total Running Ton', dataKey: 'total_running_ton', field: 'total_running_ton' },
      { header: 'Run Under Deviation', dataKey: 'deviationStatus', field: 'run_under_deviation' },
      { header: 'Price (INR)', dataKey: 'purchase_price', field: 'purchase_price' },
      { header: 'Remarks', dataKey: 'remarks', field: 'remarks' },
      { header: 'Created At', dataKey: 'created_at', field: 'created_at' },
      { header: 'Created By', dataKey: 'created_by', field: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at', field: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by', field: 'updated_by' },
    ];

    const columns = Array.isArray(checkedList)
      ? allColumns.filter(col => checkedList.includes(col.field))
      : [];

    const rows = Array.isArray(pdfdata)
      ? pdfdata.map(item => {
        const row = {};
        columns.forEach(col => {
          switch (col.field) {
            case 'tool_number':
              row.toolNumber = item?.die?.die_number
                ? `${item.die.die_number}-${item.tool_number}`
                : 'N/A';
              break;
            case 'current_kg':
              row.current_kg = item?.current_kg
                ? `${item.current_kg}`
                : 'N/A';
              break;
            case 'order_date':
              row.order_date = item?.order_date
                ? dayjs(item.order_date).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'received_date':
              row.received_date = item?.received_date
                ? dayjs(item.received_date).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'die_size':
              row.toolSize = item?.die_size?.die_width
                ? `${item.die_size.die_width} X ${item.die_size.die_height}`
                : 'N/A';
              break;
            case 'die_cavity':
              row.dieCavity = item.die_cavity || 'N/A';
              break;
            case 'is_active':
              row.status = item.is_active ? true : false;
              break;
            case 'total_running_qty':
              row.runningQty = item.total_running_qty || '0';
              break;
            case 'rac_no':
              row.rac_no = item.rac_no || '0';
              break;
            case 'row_no':
              row.row_no = item.row_no || '0';
              break;
            case 'column_no':
              row.column_no = item.column_no || '0';
              break;
            case 'developer':
              row.developer = item.developer?.vendor_registered_name || 'N/A';
              break;
            case 'ownership':
              row.ownership = item.ownership || 'N/A';
              break;
            case 'drawing_no':
              row.drawing_no = item.drawing_no || 'N/A';
              break;
            case 'developer_ref_no':
              row.developer_ref_no = item.developer_ref_no || 'N/A';
              break;
            case 'total_running_ton':
              row.total_running_ton = item.total_running_ton || '0';
              break;
            case 'run_under_deviation':
              row.deviationStatus = item.run_under_deviation
                ? deviationStatusMap[item.run_under_deviation] || 'Under Development'
                : 'N/A';
              break;
            case 'purchase_price':
              row.purchase_price =
                `${item.purchase_price?.toLocaleString({ maximumFractionDigits: 0 })}` ||
                '0';
              break;
            case 'remarks':
              row.remarks = item.remarks || 'N/A';
              break;
            case 'created_at':
              row.created_at = item.created_at
                ? moment(item.created_at).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'created_by':
              row.created_by = item.created_by
                ? `${item.created_by.first_name} ${item.created_by.last_name}`
                : 'N/A';
              break;
            case 'updated_at':
              row.updated_at = item.updated_at
                ? moment(item.updated_at).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'updated_by':
              row.updated_by = item.updated_by
                ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
                : 'N/A';
              break;
            default:
              break;
          }
        });
        return row;
      })
      : [];

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = 150;
    const imgHeight = 40;
    pdf.addImage(logo, 'PNG', pageWidth - imgWidth - 40, 20, imgWidth, imgHeight);
    pdf.setFontSize(16);
    pdf.text('Profile Tool', pageWidth / 2, 40, { align: 'center' });
    pdf.autoTable({
      columns,
      body: rows,
      startY: 80,
      theme: 'grid',
      margin: { top: 80, right: 30, left: 30, bottom: 40 },
      styles: {
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.8,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      columnStyles: {
        price: { halign: 'right' },
        runningQty: { halign: 'right' },
      },
      didDrawPage: (data) => {
        const pageHeight = pdf.internal.pageSize.getHeight();
        const pageWidth = pdf.internal.pageSize.getWidth();

        // Footer - Date & Time on left
        pdf.setFontSize(8);
        pdf.setTextColor(100);
        const now = moment();
        const dateStr = `${now.format("DD-MM-YYYY hh:mm:ss A")}`;
        pdf.text(dateStr, 40, pageHeight - 20);

        // Footer - Page number on right
        const pageStr = `Page ${data.pageNumber}`;
        pdf.text(pageStr, pageWidth - 40, pageHeight - 20, { align: "right" });
      },
      willDrawCell: pdfdata => {
        if (pdfdata.cell.text.length > 0) {
          const text = pdfdata.cell.text[0];
          if (text && text.length > 30) {
            pdfdata.cell.text[0] = text.substring(0, 27) + '...';
          }
        }
      },
    });
    pdf.save(`profile_tool${new Date().toISOString().split('T')[0]}.pdf`);
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Profile Tool',
        ip_address: ipAddress,
        discription: 'Profile tool pdf download successfully!',
      }),
    );
  };

  const csvheaders = [
    { label: 'Tool No.', key: 'tool_number' },
    { label: 'Tool Size', key: 'die_size' },
    { label: 'Current KG/MTR', key: 'current_kg' },
    { label: 'Order Date', key: 'order_date' },
    { label: 'Received Date', key: 'received_date' },
    { label: 'Die Cavity', key: 'die_cavity' },
    { label: 'Die Received Status', key: 'status' },
    { label: 'Total Running QTY (kg)', key: 'total_running_qty' },
    { label: 'Rack No.', key: 'rac_no' },
    { label: 'Row No.', key: 'row_no' },
    { label: 'Column No.', key: 'column_no' },
    { label: 'Vendor', key: 'developer' },
    { label: 'Ownership', key: 'ownership' },
    { label: 'Drawing No.', key: 'drawing_no' },
    { label: 'Total Running Metric Ton', key: 'total_running_ton' },
    { label: 'Developer Ref. No', key: 'developer_ref_no' },
    { label: 'Run Under Deviation', key: 'run_under_deviation' },
    { label: 'Purchase Price (INR)', key: 'purchase_price' },
    { label: 'Remarks', key: 'remarks' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];
  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvListData = data?.map((item, index) => ({
    tool_number: item?.die?.die_number
      ? `${item.die.die_number}-${item.tool_number}`
      : 'N/A',
    current_kg: item?.current_kg ? `${item.current_kg}` : 'N/A',
    order_date: item?.order_date ? `${item.order_date}` : 'N/A',
    received_date: item?.received_date ? `${item.received_date}` : 'N/A',
    die_size: item?.die_size.die_width
      ? `${item.die_size.die_width} X ${item.die_size.die_height}`
      : 'N/A',
    die_cavity: item?.die_cavity || 'N/A',
    status: item?.is_active ? 'True' : 'False',
    total_running_qty: item?.total_running_qty || '0',
    total_running_ton: item?.total_running_ton || '0',
    developer_ref_no: item?.developer_ref_no || 'N/A',
    drawing_no: item?.drawing_no || 'N/A',
    rac_no: item?.rac_no || '0',
    row_no: item?.row_no || '0',
    column_no: item?.column_no || '0',
    developer: item?.developer.vendor_registered_name || 'N/A',
    ownership: item?.ownership || 'N/A',
    run_under_deviation: item?.run_under_deviation
      ? deviationStatusMap[item?.run_under_deviation] || 'Under Development'
      : 'N/A',
    purchase_price:
      item?.purchase_price?.toLocaleString({ maximumFractionDigits: 0 }) || '0',
    remarks: item?.remarks || 'N/A',
    created_at: item?.created_at
      ? moment(item?.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: item?.created_by
      ? `${item?.created_by.first_name} ${item?.created_by.last_name}`
      : 'N/A',
    updated_at: item?.updated_at
      ? moment(item?.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: item?.updated_by
      ? `${item?.updated_by.first_name} ${item?.updated_by.last_name}`
      : 'N/A',
  }));

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Profile Tool',
        ip_address: ipAddress,
        discription: 'Profile tool excel download successfully!',
      }),
    );
  }
  return (
    <div className='mainwrap'>
      <div className="sticyaction">
        <Row className="pagenamerow mb-0" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Die Tools</h2>
            <div className="bredcrumbwrp">
              <Link to="/die-profilelist" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  // { title: <Link to="/die-profilelist">Profiles</Link> },
                   { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Die Tools' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className='searchbox'
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}
                style={{ width: 200 }}
              />
              <Tooltip placement="top" title="Reset Filter">
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
              {currentUser.permissions.permissions.die.includes(
                'download_profile_tool_excel_copy',
              ) && (
                  <Tooltip placement="top" title={'Export Excel'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={activityLog}
                    >
                      <CSVLink
                        filename={'profile_tool.csv'}
                        data={csvListData}
                        className="iconlink"
                        headers={csvFilterheaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.die.includes(
                'download_profile_tool_pdf_copy',
              ) && (
                  <Tooltip placement="top" title={'Export PDF'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={() => exportDieToolToPDF(data)}
                    >
                      <FilePdfOutlined />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.die.includes(
                'add_dietool',
              ) && (
                  <Link to="/add-die-tool">
                    <Tooltip placement="top" title={'Add Die Tool'}>
                      <Button type="primary" icon={<PlusOutlined />}>
                        Add Die Tool
                      </Button>
                    </Tooltip>
                  </Link>
                )}
                  {/* <Button type="primary" icon={<UploadOutlined  />} onClick={() => setOpenModal(true)}>
                                Import Data
                              </Button> */}
              {currentUser.permissions.permissions.die.includes(
                'delete_dietool',
              ) && (
                  <Tooltip placement="top" title="Archive">
                    <Link to="/archive-die-tool" className="iconlink">
                      {' '}
                      <Archiveicon style={{ cursor: 'pointer' }} />
                    </Link>
                  </Tooltip>
                )}
              <Tooltip placement="topLeft" title="Column Filter Options">
                <Dropdown
                  overlay={columnMenu}
                  trigger={['click']}
                  placement="bottomRight"
                  visible={dropdownVisible}
                  onVisibleChange={setDropdownVisible}
                >
                  <Button
                    type="primary"
                    icon={<ColumnList />}
                    onClick={e => e.preventDefault()}
                  />
                </Dropdown>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>
      {/* <ImportDataModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        refreshTable={refreshTable}
        formatFilePath={formatFile}
        model={"dietool"}
      /> */}
      <div className='tablewrp'>
        <Table
          rowKey="id"
          dataSource={data}
          columns={filteredColumns}
          pagination={false}
          loading={status === 'loading'}
          onChange={handleTableChange}
          scroll={{ x: '1000px' }}
        />
      </div>

      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={["20", "50", "100"]}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />


    </div >
  );
}
